/**
 * Value codecs: decode a store field's delimiter-encoded string to structured
 * readable JSON, and encode it back. Selection of which codec a field uses lives
 * in field-index.ts (driven by the filter config `SearchFilterType`).
 *
 * Correctness contract: the present layer only trusts a codec's output when it
 * re-encodes to the ORIGINAL string (`roundTrips`), so an imperfect codec can
 * only ever fail "readable" — never lossy. `toStore` returns `undefined` to mean
 * "omit this field" (empty list, cleared value).
 */
import {
  COMPANY_ID_FILTER_SEPARATOR,
  FILTER_VALUE_SEPARATOR,
  RANGE_FILTER_SEPARATOR,
} from '@aibees/elephant/constants/filters';
import {
  INNER_SEPARATOR,
  ITEM_SEPARATOR,
} from '../filters/employee-by-department-filter/config';
import { CodecKind, UUID_PATTERN } from './constants';

export interface ValueCodec {
  toReadable(raw: string): unknown;
  toStore(val: unknown): string | undefined;
}

// ── helpers ────────────────────────────────────────────────────────────────

/** '' -> undefined; integer/decimal string -> number; otherwise the string. */
function numify(s: string | undefined): number | string | undefined {
  if (s == null || s === '') return undefined;
  return /^-?\d+(\.\d+)?$/.test(s) ? Number(s) : s;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v != null && !Array.isArray(v);
}

/** True when a codec re-encodes `raw` byte-for-byte — the safety gate. */
export function roundTrips(codec: ValueCodec, raw: string): boolean {
  try {
    return codec.toStore(codec.toReadable(raw)) === raw;
  } catch {
    return false;
  }
}

// ── entity (name::id / name::id::extra) ──────────────────────────────────────

type EntityItem = { name: string; id?: string; extra?: string };

function decodeEntityItem(item: string): EntityItem {
  // 3-part, anchored on a UUID id: "<name>::<uuid>::<extra>"
  const matches = Array.from(item.matchAll(UUID_PATTERN));
  for (let i = matches.length - 1; i >= 0; i--) {
    const m = matches[i];
    const idx = m.index ?? -1;
    const before = item.slice(idx - 2, idx);
    const afterAt = idx + m[0].length;
    const after = item.slice(afterAt, afterAt + 2);
    if (
      before === COMPANY_ID_FILTER_SEPARATOR &&
      after === COMPANY_ID_FILTER_SEPARATOR
    ) {
      return {
        name: item.slice(0, idx - 2),
        id: m[0],
        extra: item.slice(afterAt + 2),
      };
    }
  }
  // 2-part, right-greedy: "<name>::<id>"
  const i = item.lastIndexOf(COMPANY_ID_FILTER_SEPARATOR);
  if (i === -1) return { name: item };
  return { name: item.slice(0, i), id: item.slice(i + 2) };
}

function encodeEntityItem(v: EntityItem): string {
  const sep = COMPANY_ID_FILTER_SEPARATOR;
  if (v.extra != null) return `${v.name}${sep}${v.id ?? ''}${sep}${v.extra}`;
  if (v.id != null && v.id !== '') return `${v.name}${sep}${v.id}`;
  return v.name;
}

// ── codecs by kind ───────────────────────────────────────────────────────────

const listCodec: ValueCodec = {
  toReadable: (raw) => raw.split(FILTER_VALUE_SEPARATOR),
  toStore: (val) =>
    Array.isArray(val) && val.length
      ? val.map(String).join(FILTER_VALUE_SEPARATOR)
      : undefined,
};

const rangeCodec: ValueCodec = {
  toReadable: (raw) => {
    const [min, max] = raw.split(RANGE_FILTER_SEPARATOR);
    const out: Record<string, number | string> = {};
    const lo = numify(min);
    const hi = numify(max);
    if (lo !== undefined) out.min = lo;
    if (hi !== undefined) out.max = hi;
    return out;
  },
  toStore: (val) => {
    if (!isPlainObject(val)) return undefined;
    const min = val.min ?? '';
    const max = val.max ?? '';
    if (min === '' && max === '') return undefined;
    return `${min}${RANGE_FILTER_SEPARATOR}${max}`;
  },
};

function durationSide(part: string | undefined): {
  years?: number | string;
  months?: number | string;
} {
  const [y, m] = (part ?? '').split(':');
  const out: { years?: number | string; months?: number | string } = {};
  const yy = numify(y);
  const mm = numify(m);
  if (yy !== undefined) out.years = yy;
  if (mm !== undefined) out.months = mm;
  return out;
}

const durationCodec: ValueCodec = {
  toReadable: (raw) => {
    const [lo, hi] = raw.split(RANGE_FILTER_SEPARATOR);
    return { from: durationSide(lo), to: durationSide(hi) };
  },
  toStore: (val) => {
    if (!isPlainObject(val)) return undefined;
    const side = (d: unknown) => {
      const o = isPlainObject(d) ? d : {};
      return `${o.years ?? ''}:${o.months ?? ''}`;
    };
    return `${side(val.from)}${RANGE_FILTER_SEPARATOR}${side(val.to)}`;
  },
};

const entityCodec: ValueCodec = {
  toReadable: (raw) => raw.split(FILTER_VALUE_SEPARATOR).map(decodeEntityItem),
  toStore: (val) =>
    Array.isArray(val) && val.length
      ? val
          .map((v) => encodeEntityItem(v as EntityItem))
          .join(FILTER_VALUE_SEPARATOR)
      : undefined,
};

const treeCodec: ValueCodec = {
  toReadable: (raw) =>
    raw
      .split(FILTER_VALUE_SEPARATOR)
      .map((p) => p.split(COMPANY_ID_FILTER_SEPARATOR)),
  toStore: (val) =>
    Array.isArray(val) && val.length
      ? val
          .map((path) =>
            (path as string[]).map(String).join(COMPANY_ID_FILTER_SEPARATOR)
          )
          .join(FILTER_VALUE_SEPARATOR)
      : undefined,
};

const matchMapCodec: ValueCodec = {
  toReadable: (raw) => {
    const out: Record<string, string> = {};
    for (const part of raw.split(FILTER_VALUE_SEPARATOR)) {
      const i = part.indexOf(':');
      if (i === -1) continue;
      out[part.slice(0, i)] = part.slice(i + 1);
    }
    return out;
  },
  toStore: (val) => {
    if (!isPlainObject(val)) return undefined;
    const entries = Object.entries(val);
    if (!entries.length) return undefined;
    return entries.map(([k, v]) => `${k}:${v}`).join(FILTER_VALUE_SEPARATOR);
  },
};

const booleanCodec: ValueCodec = {
  toReadable: (raw) => (raw === 'true' ? true : raw === 'false' ? false : raw),
  toStore: (val) =>
    val === true
      ? 'true'
      : val === false
      ? 'false'
      : typeof val === 'string'
      ? val
      : undefined,
};

const singleCodec: ValueCodec = {
  toReadable: (raw) => raw,
  toStore: (val) =>
    typeof val === 'string' ? val : val == null ? undefined : String(val),
};

// ── employee-by-department / headcount-growth ────────────────────────────────
// Row: departments(^-joined) | min>max [| timeFrame (growth only)] [| type]
// Rows joined by ITEM_SEPARATOR. `type` codes: INC=include, EXC=exclude,
// INC_AND=includeAnd (see employee-by-department-filter/query-type.ts).

const QT_TO_READABLE: Record<string, string> = {
  INC: 'include',
  EXC: 'exclude',
  INC_AND: 'includeAnd',
};
const QT_FROM_READABLE = invert(QT_TO_READABLE);

type DeptRow = {
  departments: string[];
  min?: number | string;
  max?: number | string;
  timeFrame?: string;
  type?: string;
};

function makeDeptCodec(withTimeFrame: boolean): ValueCodec {
  const typeIndex = withTimeFrame ? 3 : 2;
  return {
    toReadable: (raw) =>
      raw.split(ITEM_SEPARATOR).map((row) => {
        const seg = row.split(INNER_SEPARATOR);
        const [min, max] = (seg[1] ?? '').split(RANGE_FILTER_SEPARATOR);
        const out: DeptRow = {
          departments: (seg[0] ?? '').split(FILTER_VALUE_SEPARATOR),
        };
        const lo = numify(min);
        const hi = numify(max);
        if (lo !== undefined) out.min = lo;
        if (hi !== undefined) out.max = hi;
        if (withTimeFrame && seg[2]) out.timeFrame = seg[2];
        const qt = seg[typeIndex];
        if (qt && QT_TO_READABLE[qt]) out.type = QT_TO_READABLE[qt];
        return out;
      }),
    toStore: (val) => {
      if (!Array.isArray(val) || !val.length) return undefined;
      return val
        .map((r) => {
          const row = (isPlainObject(r) ? r : {}) as DeptRow;
          const deps = Array.isArray(row.departments)
            ? row.departments.map(String).join(FILTER_VALUE_SEPARATOR)
            : '';
          const range = `${row.min ?? ''}${RANGE_FILTER_SEPARATOR}${
            row.max ?? ''
          }`;
          const segs = [deps, range];
          if (withTimeFrame) segs.push(row.timeFrame ?? '');
          const qt = row.type ? QT_FROM_READABLE[row.type] : undefined;
          if (qt) {
            while (segs.length < typeIndex) segs.push('');
            segs[typeIndex] = qt;
          }
          return segs.join(INNER_SEPARATOR);
        })
        .join(ITEM_SEPARATOR);
    },
  };
}

const employeeByDepartmentCodec = makeDeptCodec(false);
const headcountGrowthCodec = makeDeptCodec(true);

// ── keyword sources + per-source match (readable labels) ─────────────────────
// Sources and match modes are stored as short codes; show the same labels the
// UI uses. Unknown codes pass through unchanged (still round-trips).

const SOURCE_LABELS: Record<string, string> = {
  // company
  NAME: 'Company Name',
  KEYWORD: 'Product & Services',
  SEO: 'SEO Description',
  DESCRIPTION: 'Company Description',
  INDUSTRY: 'Industry',
  // people
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  HEADLINE: 'Headline',
  SUMMARY: 'Summary',
  WORK_HISTORY_DESCRIPTION: 'Work History',
  EDUCATION_DESCRIPTION: 'Education',
  VOLUNTEERING: 'Volunteering',
  PUBLICATION: 'Publication',
  COURSE: 'Course',
  AWARD: 'Award',
  PATENT: 'Patent',
  TEST_SCORE: 'Test Score',
  ORGANIZATION: 'Organization',
  PROJECTS: 'Projects',
  SKILL: 'Skills',
  LANGUAGE_SKILL: 'Language Skills',
  CERTIFICATION: 'Certificates',
};
const SOURCE_CODES = invert(SOURCE_LABELS);

// Same vocabulary as the search-match button tooltips
// (filter-exact-match/config.ts: "Search Smart/Word/Strict Match").
const MATCH_LABELS: Record<string, string> = {
  SMART: 'Smart Match',
  WORD: 'Word Match',
  STRICT: 'Strict Match',
};
const MATCH_CODES = invert(MATCH_LABELS);

const keywordSourcesCodec: ValueCodec = {
  toReadable: (raw) =>
    raw.split(FILTER_VALUE_SEPARATOR).map((c) => SOURCE_LABELS[c] ?? c),
  toStore: (val) =>
    Array.isArray(val) && val.length
      ? val
          .map((l) => SOURCE_CODES[String(l)] ?? String(l))
          .join(FILTER_VALUE_SEPARATOR)
      : undefined,
};

const keywordMatchCodec: ValueCodec = {
  toReadable: (raw) => {
    const out: Record<string, string> = {};
    for (const part of raw.split(FILTER_VALUE_SEPARATOR)) {
      const i = part.indexOf(':');
      if (i === -1) continue;
      const src = part.slice(0, i);
      const mode = part.slice(i + 1);
      out[SOURCE_LABELS[src] ?? src] = MATCH_LABELS[mode] ?? mode;
    }
    return out;
  },
  toStore: (val) => {
    if (!isPlainObject(val)) return undefined;
    const entries = Object.entries(val);
    if (!entries.length) return undefined;
    return entries
      .map(
        ([k, v]) =>
          `${SOURCE_CODES[k] ?? k}:${MATCH_CODES[String(v)] ?? String(v)}`
      )
      .join(FILTER_VALUE_SEPARATOR);
  },
};

function invert(map: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(map)) out[v] = k;
  return out;
}

export const CODECS: Record<CodecKind, ValueCodec> = {
  [CodecKind.LIST]: listCodec,
  [CodecKind.RANGE]: rangeCodec,
  [CodecKind.DURATION]: durationCodec,
  [CodecKind.ENTITY]: entityCodec,
  [CodecKind.TREE]: treeCodec,
  [CodecKind.MATCH_MAP]: matchMapCodec,
  [CodecKind.BOOLEAN]: booleanCodec,
  [CodecKind.SINGLE]: singleCodec,
  [CodecKind.EMPLOYEE_BY_DEPARTMENT]: employeeByDepartmentCodec,
  [CodecKind.HEADCOUNT_GROWTH]: headcountGrowthCodec,
  [CodecKind.KEYWORD_SOURCES]: keywordSourcesCodec,
  [CodecKind.KEYWORD_MATCH]: keywordMatchCodec,
};
